import QRCode from 'qrcode';

export interface BrandedQrOptions {
  width?: number;
  margin?: number;
  foreground?: string;
  background?: string;
  finderColor?: string;
  accentColor?: string;
  logoUrl?: string;
  logoSizeRatio?: number; // default ~0.22 (22% of QR width)
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  moduleRadius?: number;
  transparentLogo?: boolean;
}

/**
 * Calculates relative luminance according to W3C standards.
 */
export function getLuminance(hexColor: string): number {
  try {
    let clean = hexColor.replace('#', '').trim();
    if (clean.length === 3) {
      clean = clean.split('').map((c) => c + c).join('');
    }
    if (clean.length !== 6) return 0;
    const num = parseInt(clean, 16);
    const r = ((num >> 16) & 255) / 255;
    const g = ((num >> 8) & 255) / 255;
    const b = (num & 255) / 255;

    const sRGB = [r, g, b].map((val) =>
      val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
    );
    return sRGB[0] * 0.2126 + sRGB[1] * 0.7152 + sRGB[2] * 0.0722;
  } catch {
    return 0;
  }
}

/**
 * Calculates contrast ratio between two hex colors.
 */
export function getContrastRatio(hex1: string, hex2: string): number {
  const lum1 = getLuminance(hex1);
  const lum2 = getLuminance(hex2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

/**
 * Enforces safe contrast for reliable QR scannability.
 * QR scanners require high contrast between dark modules and light background.
 */
export function ensureSafeQrColors(
  brandFg = '#0284c7',
  brandBg = '#ffffff'
): { foreground: string; background: string } {
  const contrast = getContrastRatio(brandFg, brandBg);
  // Minimum safe contrast ratio for QR code readability is 3.5:1
  if (contrast < 3.5) {
    // If brand foreground is too light on white, fallback to deep slate
    return {
      foreground: '#0f172a',
      background: '#ffffff',
    };
  }
  return {
    foreground: brandFg,
    background: brandBg,
  };
}

/**
 * Helper to load an HTML image asynchronously.
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

/**
 * Generates a clean vector SVG for the branded QR code with
 * center safe clearance, transparent logo embedding, and quiet zone.
 */
export function generateBrandedQrSvg(
  text: string,
  options?: BrandedQrOptions
): string {
  const errorCorrectionLevel = options?.errorCorrectionLevel || 'H';
  const qr = QRCode.create(text, { errorCorrectionLevel });
  const size = qr.modules.size;
  const margin = options?.margin ?? 4;
  const totalModules = size + margin * 2;
  const mod = 20;
  const pixelSize = totalModules * mod;

  const rawFg = options?.foreground || '#04903c';
  const rawBg = options?.background || '#ffffff';
  const { foreground, background } = ensureSafeQrColors(rawFg, rawBg);
  const finderColor = options?.finderColor || options?.accentColor || '#0238a2';
  const eyeColor = options?.accentColor || foreground;

  function isFinder(r: number, c: number): boolean {
    if (r <= 7 && c <= 7) return true;
    if (r <= 7 && c >= size - 8) return true;
    if (r >= size - 8 && c <= 7) return true;
    return false;
  }

  function isCenterSafe(r: number, c: number): boolean {
    if (!options?.logoUrl) return false;
    const mid = Math.floor(size / 2);
    // Center clearance of 7x7 modules
    return Math.abs(r - mid) <= 3 && Math.abs(c - mid) <= 3;
  }

  let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${pixelSize} ${pixelSize}" width="100%" height="100%">`;
  svg += `<rect width="${pixelSize}" height="${pixelSize}" fill="${background}"/>`;

  // 1. Finder Patterns
  const finders = [
    { r: 0, c: 0 },
    { r: 0, c: size - 7 },
    { r: size - 7, c: 0 },
  ];

  finders.forEach((f) => {
    const fx = (margin + f.c) * mod;
    const fy = (margin + f.r) * mod;
    svg += `<rect x="${fx}" y="${fy}" width="140" height="140" rx="16" ry="16" fill="${finderColor}"/>`;
    svg += `<rect x="${fx + 20}" y="${fy + 20}" width="100" height="100" rx="8" ry="8" fill="${background}"/>`;
    svg += `<rect x="${fx + 40}" y="${fy + 40}" width="60" height="60" rx="6" ry="6" fill="${eyeColor}"/>`;
  });

  // 2. Data Modules
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (isFinder(r, c)) continue;
      if (isCenterSafe(r, c)) continue;
      if (qr.modules.get(r, c)) {
        const mx = (margin + c) * mod;
        const my = (margin + r) * mod;
        svg += `<rect x="${mx}" y="${my}" width="${mod}" height="${mod}" rx="4" ry="4" fill="${foreground}"/>`;
      }
    }
  }

  // 3. Center Logo (Transparent - No white rectangle card)
  if (options?.logoUrl) {
    const logoW = 120;
    const logoH = 87; // standard aspect ratio
    const logoX = pixelSize / 2 - logoW / 2;
    const logoY = pixelSize / 2 - logoH / 2;
    svg += `<image href="${options.logoUrl}" x="${logoX}" y="${logoY}" width="${logoW}" height="${logoH}"/>`;
  }

  svg += `</svg>`;
  return svg;
}

/**
 * Generates a high-resolution, branded QR code Data URL (PNG)
 * with contrast verification, center clearance, and transparent logo overlay.
 */
export async function generateQrDataUrl(
  text: string,
  options?: BrandedQrOptions
): Promise<string> {
  const size = options?.width || 400;
  const margin = options?.margin ?? 4;
  const rawFg = options?.foreground || '#04903c';
  const rawBg = options?.background || '#ffffff';
  const { foreground, background } = ensureSafeQrColors(rawFg, rawBg);
  const errorCorrectionLevel = options?.errorCorrectionLevel || 'H';
  const finderColor = options?.finderColor || options?.accentColor || '#0238a2';
  const eyeColor = options?.accentColor || foreground;

  // If no logo requested or running in environment without DOM Canvas
  if (!options?.logoUrl || typeof document === 'undefined') {
    return await QRCode.toDataURL(text, {
      width: size,
      margin,
      color: {
        dark: foreground,
        light: background,
      },
      errorCorrectionLevel,
    });
  }

  try {
    const qr = QRCode.create(text, { errorCorrectionLevel });
    const matrixSize = qr.modules.size;
    const totalModules = matrixSize + margin * 2;

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return await QRCode.toDataURL(text, {
        width: size,
        margin,
        color: { dark: foreground, light: background },
        errorCorrectionLevel,
      });
    }

    const mod = size / totalModules;

    // Fill background
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, size, size);

    function isFinder(r: number, c: number): boolean {
      if (r <= 7 && c <= 7) return true;
      if (r <= 7 && c >= matrixSize - 8) return true;
      if (r >= matrixSize - 8 && c <= 7) return true;
      return false;
    }

    const mid = Math.floor(matrixSize / 2);
    function isCenterSafe(r: number, c: number): boolean {
      return Math.abs(r - mid) <= 3 && Math.abs(c - mid) <= 3;
    }

    // 1. Draw Finder Patterns
    const finders = [
      { r: 0, c: 0 },
      { r: 0, c: matrixSize - 7 },
      { r: matrixSize - 7, c: 0 },
    ];

    finders.forEach((f) => {
      const fx = (margin + f.c) * mod;
      const fy = (margin + f.r) * mod;

      // Outer frame
      ctx.fillStyle = finderColor;
      ctx.beginPath();
      ctx.roundRect(fx, fy, 7 * mod, 7 * mod, mod * 0.8);
      ctx.fill();

      // Inner white separator
      ctx.fillStyle = background;
      ctx.beginPath();
      ctx.roundRect(fx + mod, fy + mod, 5 * mod, 5 * mod, mod * 0.4);
      ctx.fill();

      // Inner solid eye
      ctx.fillStyle = eyeColor;
      ctx.beginPath();
      ctx.roundRect(fx + 2 * mod, fy + 2 * mod, 3 * mod, 3 * mod, mod * 0.3);
      ctx.fill();
    });

    // 2. Draw Data Modules with subtle rounding
    ctx.fillStyle = foreground;
    for (let r = 0; r < matrixSize; r++) {
      for (let c = 0; c < matrixSize; c++) {
        if (isFinder(r, c)) continue;
        if (isCenterSafe(r, c)) continue;
        if (qr.modules.get(r, c)) {
          const mx = (margin + c) * mod;
          const my = (margin + r) * mod;
          ctx.beginPath();
          ctx.roundRect(mx, my, mod, mod, mod * 0.2);
          ctx.fill();
        }
      }
    }

    // 3. Load & Draw Logo with 100% Transparent Background (NO opaque white box)
    const logoImg = await loadImage(options.logoUrl);
    const centerSpan = 7 * mod;
    const maxLogoW = centerSpan * 0.9;
    const maxLogoH = centerSpan * 0.9;

    const imgAspect = (logoImg.width || 1) / (logoImg.height || 1);
    let drawW = maxLogoW;
    let drawH = maxLogoW / imgAspect;

    if (drawH > maxLogoH) {
      drawH = maxLogoH;
      drawW = maxLogoH * imgAspect;
    }

    const drawX = (size - drawW) / 2;
    const drawY = (size - drawH) / 2;

    ctx.drawImage(logoImg, drawX, drawY, drawW, drawH);

    return canvas.toDataURL('image/png');
  } catch (error) {
    console.warn('Branded QR composite fallback to basic QR:', error);
    return await QRCode.toDataURL(text, {
      width: size,
      margin,
      color: { dark: foreground, light: background },
      errorCorrectionLevel,
    });
  }
}
